import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, FileJson, ImageIcon, Upload, Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import "../../assets/styles/colors.css";

/**
 * Modal with two import options: Upload JSON or Upload Image.
 * - JSON: triggers the existing JSON import flow via onImportJson(file).
 * - Image: uploads to /api/v1/topology/chat/upload via onImportImage(file).
 */
function ImportOptionsModal({
  show,
  onClose,
  onImportJson,
  onImportImage,
  jsonUploading = false,
  imageUploading = false,
}) {
  const { t } = useTranslation();
  const jsonInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  if (!show) return null;

  const handleJsonSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onImportJson(file);
    e.target.value = "";
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onImportImage(file);
    e.target.value = "";
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    setDragOverTarget(null);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (type === "json") {
      onImportJson(file);
    } else {
      onImportImage(file);
    }
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    setDragOverTarget(type);
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  return (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex flex-col w-[90vw] max-w-lg rounded-xl border border-(--color-border) bg-(--color-bg-card) shadow-[0_0_20px_var(--color-shadow-primary)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-(--color-border) px-6 py-4 shrink-0">
          <h3 className="text-lg font-semibold text-(--color-text-primary)">
            {t("Import Topology")}
          </h3>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body: two options */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Upload JSON */}
          <button
            type="button"
            disabled={jsonUploading || imageUploading}
            className={`group relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 transition-all ${
              jsonUploading
                ? "cursor-wait opacity-70"
                : imageUploading
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
            } ${
              dragOverTarget === "json"
                ? "border-(--color-primary) bg-(--color-primary)/10"
                : "border-(--color-border) hover:border-(--color-primary)/60 hover:bg-(--color-primary)/5"
            }`}
            onClick={() =>
              !jsonUploading && !imageUploading && jsonInputRef.current?.click()
            }
            onDrop={(e) =>
              !jsonUploading && !imageUploading && handleDrop(e, "json")
            }
            onDragOver={(e) => handleDragOver(e, "json")}
            onDragLeave={handleDragLeave}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-(--color-primary)/30 bg-(--color-primary)/10">
              {jsonUploading ? (
                <Loader2 className="h-6 w-6 text-(--color-primary) animate-spin" />
              ) : (
                <FileJson className="h-6 w-6 text-(--color-primary)" />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-(--color-text-primary)">
                {jsonUploading ? t("Uploading...") : t("Upload JSON")}
              </p>
              <p className="mt-1 text-xs text-(--color-text-muted)">
                {jsonUploading
                  ? t("Processing your topology file")
                  : t("Import a topology JSON file")}
              </p>
            </div>
            <input
              type="file"
              ref={jsonInputRef}
              accept=".json,application/json"
              className="hidden"
              onChange={handleJsonSelect}
            />
          </button>

          {/* Upload Image */}
          <button
            type="button"
            disabled={imageUploading || jsonUploading}
            className={`group relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 transition-all ${
              imageUploading
                ? "cursor-wait opacity-70"
                : jsonUploading
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
            } ${
              dragOverTarget === "image"
                ? "border-(--color-primary) bg-(--color-primary)/10"
                : "border-(--color-border) hover:border-(--color-primary)/60 hover:bg-(--color-primary)/5"
            }`}
            onClick={() =>
              !imageUploading &&
              !jsonUploading &&
              imageInputRef.current?.click()
            }
            onDrop={(e) =>
              !imageUploading && !jsonUploading && handleDrop(e, "image")
            }
            onDragOver={(e) => handleDragOver(e, "image")}
            onDragLeave={handleDragLeave}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-(--color-primary)/30 bg-(--color-primary)/10">
              {imageUploading ? (
                <Loader2 className="h-6 w-6 text-(--color-primary) animate-spin" />
              ) : (
                <ImageIcon className="h-6 w-6 text-(--color-primary)" />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-(--color-text-primary)">
                {imageUploading ? t("Uploading...") : t("Upload Image")}
              </p>
              <p className="mt-1 text-xs text-(--color-text-muted)">
                {imageUploading
                  ? t("Processing your topology image")
                  : t("Extract topology from an image")}
              </p>
            </div>
            <input
              type="file"
              ref={imageInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleImageSelect}
            />
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-(--color-border) px-6 py-3">
          <p className="text-xs text-(--color-text-muted)">
            {t("Drag & drop or click to select a file")}
          </p>
        </div>
      </div>
    </div>
  );
}

ImportOptionsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImportJson: PropTypes.func.isRequired,
  onImportImage: PropTypes.func.isRequired,
  jsonUploading: PropTypes.bool,
  imageUploading: PropTypes.bool,
};

export default ImportOptionsModal;
